import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type {
  PvPState,
  PvPRoom,
  PendingBattleOptions,
  PvpBattleFinishedLogEntry,
  PvpAcceptedRematch,
  PvpIncomingRematchRequest,
  PvpIncomingDirectChallengeRequest,
  PvpIncomingFriendRequest,
  PvpFriendRequestDeclinedNotice,
  DirectChallengeJoinerLock,
  DirectChallengeAwaitingJoinInfo,
  PvpMmrLeaderboardEntry,
  PvpMmrLeaderboardListRow,
  PvpRankedBattleFeedEntry,
  PvpTodayRankedChampions,
  PvpActiveRankedBattleEntry,
} from './state'
import { isPvpMmrLeaderboardEllipsisRow } from './state'
import initialState from './state'

export const pvpSlice = createSlice({
  name: 'pvp',
  initialState,
  reducers: {
    setLoading: (state: PvPState, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state: PvPState, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setRefreshing: (state: PvPState, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload
    },
    setRooms: (state: PvPState, action: PayloadAction<{ rooms: PvPRoom[], myRooms: PvPRoom[] }>) => {
      state.rooms = action.payload.rooms
      state.myRooms = action.payload.myRooms
    },
    setCurrentRoom: (state: PvPState, action: PayloadAction<PvPRoom | null>) => {
      state.currentRoom = action.payload
    },
    updateRoom: (state: PvPState, action: PayloadAction<PvPRoom>) => {
      const updatedRoom = action.payload
      
      // Update in rooms list
      const roomIndex = state.rooms.findIndex(r => r.roomId === updatedRoom.roomId)
      if (roomIndex !== -1) {
        state.rooms[roomIndex] = updatedRoom
      }
      
      // Update in myRooms list
      const myRoomIndex = state.myRooms.findIndex(r => r.roomId === updatedRoom.roomId)
      if (myRoomIndex !== -1) {
        state.myRooms[myRoomIndex] = updatedRoom
      } else if (updatedRoom.isCreator || updatedRoom.isJoiner) {
        // Add to myRooms if it's user's room
        state.myRooms.push(updatedRoom)
      }
      
      // Update current room if it's the same
      if (state.currentRoom?.roomId === updatedRoom.roomId) {
        state.currentRoom = updatedRoom
      }
    },
    addRoom: (state: PvPState, action: PayloadAction<PvPRoom>) => {
      const room = action.payload
      
      // Add to rooms if status is waiting
      if (room.status === 'waiting') {
        const exists = state.rooms.some(r => r.roomId === room.roomId)
        if (!exists) {
          state.rooms.push(room)
        }
      }
      
      // Add to myRooms if it's user's room
      if (room.isCreator || room.isJoiner) {
        const exists = state.myRooms.some(r => r.roomId === room.roomId)
        if (!exists) {
          state.myRooms.push(room)
        }
      }
    },
    removeRoom: (state: PvPState, action: PayloadAction<string>) => {
      const roomId = action.payload
      state.rooms = state.rooms.filter(r => r.roomId !== roomId)
      state.myRooms = state.myRooms.filter(r => r.roomId !== roomId)
      
      if (state.currentRoom?.roomId === roomId) {
        state.currentRoom = null
      }
    },
    clearCurrentRoom: (state: PvPState) => {
      state.currentRoom = null
    },
    clearError: (state: PvPState) => {
      state.error = null
    },
    setPendingBattle: (state: PvPState, action: PayloadAction<{ battleOptions: PendingBattleOptions; selectedCharacters: any[] }>) => {
      state.pendingBattle = action.payload
    },
    setPendingCharacters: (state: PvPState, action: PayloadAction<any[]>) => {
      state.pendingBattle.selectedCharacters = action.payload
    },
    clearPendingBattle: (state: PvPState) => {
      state.pendingBattle = { battleOptions: null, selectedCharacters: [] }
    },
    setTournamentLobbyMode: (state: PvPState, action: PayloadAction<boolean>) => {
      state.isTournamentLobby = action.payload
    },
    setPlayLobbyLockedMode: (state: PvPState, action: PayloadAction<'ranked' | 'unranked' | null>) => {
      state.playLobbyLockedMode = action.payload
    },
    setBattleFinishedLogsLoading: (state: PvPState, action: PayloadAction<boolean>) => {
      state.battleFinishedLogsLoading = action.payload
    },
    setBattleFinishedLogs: (
      state: PvPState,
      action: PayloadAction<{ logs: PvpBattleFinishedLogEntry[]; total: number }>
    ) => {
      state.battleFinishedLogs = action.payload.logs
      state.battleFinishedLogsTotal = action.payload.total
    },
    appendBattleFinishedLogs: (
      state: PvPState,
      action: PayloadAction<{ logs: PvpBattleFinishedLogEntry[]; total: number }>
    ) => {
      const seen = new Set(state.battleFinishedLogs.map((l) => l.battleId))
      for (const log of action.payload.logs) {
        if (!seen.has(log.battleId)) {
          seen.add(log.battleId)
          state.battleFinishedLogs.push(log)
        }
      }
      state.battleFinishedLogsTotal = action.payload.total
    },
    setLiveRankedBattleFeed: (state: PvPState, action: PayloadAction<PvpRankedBattleFeedEntry[]>) => {
      state.liveRankedBattleFeed = action.payload
    },
    setLiveRankedBattleFeedLoading: (state: PvPState, action: PayloadAction<boolean>) => {
      state.liveRankedBattleFeedLoading = action.payload
    },
    setActiveRankedBattles: (state: PvPState, action: PayloadAction<PvpActiveRankedBattleEntry[]>) => {
      state.activeRankedBattles = action.payload
    },
    setActiveRankedBattlesLoading: (state: PvPState, action: PayloadAction<boolean>) => {
      state.activeRankedBattlesLoading = action.payload
    },
    prependActiveRankedBattleEntry: (state: PvPState, action: PayloadAction<PvpActiveRankedBattleEntry>) => {
      const id = action.payload.battleId
      state.activeRankedBattles = state.activeRankedBattles.filter((e) => e.battleId !== id)
      state.activeRankedBattles.unshift(action.payload)
      if (state.activeRankedBattles.length > 80) {
        state.activeRankedBattles.length = 80
      }
    },
    removeActiveRankedBattleEntry: (state: PvPState, action: PayloadAction<string>) => {
      state.activeRankedBattles = state.activeRankedBattles.filter((e) => e.battleId !== action.payload)
    },
    setTodayRankedBattleChampions: (
      state: PvPState,
      action: PayloadAction<PvpTodayRankedChampions | null>
    ) => {
      state.todayRankedBattleChampions = action.payload
    },
    setTodayRankedBattleChampionsLoading: (state: PvPState, action: PayloadAction<boolean>) => {
      state.todayRankedBattleChampionsLoading = action.payload
    },
    setPvpMmrLeaderboard: (
      state: PvPState,
      action: PayloadAction<{
        walletAddress: string
        entries: PvpMmrLeaderboardListRow[]
        total: number
        viewerEntry: PvpMmrLeaderboardEntry | null
        snapshotMode?: boolean
      }>
    ) => {
      state.pvpMmrLeaderboardWalletAddress = action.payload.walletAddress
      state.pvpMmrLeaderboardEntries = action.payload.entries
      state.pvpMmrLeaderboardBotShowcase = []
      state.pvpMmrLeaderboardTotal = action.payload.total
      state.pvpMmrLeaderboardSnapshotMode = action.payload.snapshotMode ?? false
      state.pvpMmrLeaderboardViewerEntry = action.payload.viewerEntry
    },
    appendPvpMmrLeaderboardEntries: (
      state: PvPState,
      action: PayloadAction<{
        walletAddress: string
        entries: PvpMmrLeaderboardListRow[]
        total: number
        viewerEntry?: PvpMmrLeaderboardEntry | null
        snapshotMode?: boolean
      }>
    ) => {
      if (state.pvpMmrLeaderboardSnapshotMode) return
      if (state.pvpMmrLeaderboardWalletAddress !== action.payload.walletAddress) {
        state.pvpMmrLeaderboardWalletAddress = action.payload.walletAddress
        state.pvpMmrLeaderboardEntries = []
      }
      const seen = new Set(
        state.pvpMmrLeaderboardEntries
          .filter((e): e is PvpMmrLeaderboardEntry => !isPvpMmrLeaderboardEllipsisRow(e))
          .map((e) => e.walletAddress.toLowerCase()),
      )
      let ellipsisAppended = state.pvpMmrLeaderboardEntries.some(isPvpMmrLeaderboardEllipsisRow)
      for (const entry of action.payload.entries) {
        if (isPvpMmrLeaderboardEllipsisRow(entry)) {
          if (!ellipsisAppended) {
            ellipsisAppended = true
            state.pvpMmrLeaderboardEntries.push(entry)
          }
          continue
        }
        const key = entry.walletAddress.toLowerCase()
        if (key && !seen.has(key)) {
          seen.add(key)
          state.pvpMmrLeaderboardEntries.push(entry)
        }
      }
      state.pvpMmrLeaderboardTotal = action.payload.total
      if (action.payload.snapshotMode !== undefined) {
        state.pvpMmrLeaderboardSnapshotMode = action.payload.snapshotMode
      }
      if (action.payload.viewerEntry !== undefined) {
        state.pvpMmrLeaderboardViewerEntry = action.payload.viewerEntry
      }
    },
    appendLiveRankedBattleFeed: (state: PvPState, action: PayloadAction<PvpRankedBattleFeedEntry[]>) => {
      const seen = new Set(state.liveRankedBattleFeed.map((e) => e.battleId))
      for (const entry of action.payload) {
        if (!seen.has(entry.battleId)) {
          seen.add(entry.battleId)
          state.liveRankedBattleFeed.push(entry)
        }
      }
      if (state.liveRankedBattleFeed.length > 80) {
        state.liveRankedBattleFeed.length = 80
      }
    },
    prependLiveRankedBattleFeedEntry: (state: PvPState, action: PayloadAction<PvpRankedBattleFeedEntry>) => {
      const id = action.payload.battleId
      if (state.liveRankedBattleFeed.some((e) => e.battleId === id)) return
      state.liveRankedBattleFeed.unshift(action.payload)
      if (state.liveRankedBattleFeed.length > 80) {
        state.liveRankedBattleFeed.length = 80
      }
    },
    setRematchRequestPendingBattleId: (state: PvPState, action: PayloadAction<string | null>) => {
      state.rematchRequestPendingBattleId = action.payload
    },
    markRematchAttemptUsed: (state: PvPState, action: PayloadAction<string>) => {
      if (!action.payload || state.usedRematchBattleIds.includes(action.payload)) return
      state.usedRematchBattleIds.push(action.payload)
    },
    setIncomingRematchRequest: (
      state: PvPState,
      action: PayloadAction<PvpIncomingRematchRequest | null>
    ) => {
      state.incomingRematchRequest = action.payload
    },
    setIsRespondingToRematch: (state: PvPState, action: PayloadAction<boolean>) => {
      state.isRespondingToRematch = action.payload
    },
    setAcceptedRematch: (state: PvPState, action: PayloadAction<PvpAcceptedRematch | null>) => {
      state.acceptedRematch = action.payload
    },
    setAcceptedRematchCountdown: (state: PvPState, action: PayloadAction<number | null>) => {
      state.acceptedRematchCountdown = action.payload
    },
    setDeclinedRematchMessage: (state: PvPState, action: PayloadAction<string | null>) => {
      state.declinedRematchMessage = action.payload
    },
    setIncomingDirectChallenge: (
      state: PvPState,
      action: PayloadAction<PvpIncomingDirectChallengeRequest | null>,
    ) => {
      state.incomingDirectChallenge = action.payload
    },
    setDirectChallengeRespondBusy: (state: PvPState, action: PayloadAction<boolean>) => {
      state.directChallengeRespondBusy = action.payload
    },
    setDirectChallengeAwaitingJoin: (
      state: PvPState,
      action: PayloadAction<DirectChallengeAwaitingJoinInfo | null>,
    ) => {
      state.directChallengeAwaitingJoin = action.payload
    },
    setDirectChallengeDeclinedMessage: (state: PvPState, action: PayloadAction<string | null>) => {
      state.directChallengeDeclinedMessage = action.payload
    },
    setDirectChallengeJoinerLock: (state: PvPState, action: PayloadAction<DirectChallengeJoinerLock | null>) => {
      state.directChallengeJoinerLock = action.payload
    },
    setDirectChallengeActiveRoomId: (state: PvPState, action: PayloadAction<string | null>) => {
      state.directChallengeActiveRoomId = action.payload
    },
    setDirectChallengeBattleStartPending: (state: PvPState, action: PayloadAction<boolean>) => {
      state.directChallengeBattleStartPending = action.payload
    },
    clearDirectChallengeFlow: (state: PvPState) => {
      state.incomingDirectChallenge = null
      state.directChallengeRespondBusy = false
      state.directChallengeAwaitingJoin = null
      state.directChallengeDeclinedMessage = null
      state.directChallengeJoinerLock = null
      state.directChallengeActiveRoomId = null
      state.directChallengeBattleStartPending = false
    },
    setIncomingFriendRequest: (
      state: PvPState,
      action: PayloadAction<PvpIncomingFriendRequest | null>,
    ) => {
      state.incomingFriendRequest = action.payload
    },
    setFriendRequestRespondBusy: (state: PvPState, action: PayloadAction<boolean>) => {
      state.friendRequestRespondBusy = action.payload
    },
    setFriendRequestDeclinedNotice: (
      state: PvPState,
      action: PayloadAction<PvpFriendRequestDeclinedNotice | null>,
    ) => {
      state.friendRequestDeclinedNotice = action.payload
    },
  },
})

export const {
  setLoading,
  setError,
  setRefreshing,
  setRooms,
  setCurrentRoom,
  updateRoom,
  addRoom,
  removeRoom,
  clearCurrentRoom,
  clearError,
  setPendingBattle,
  setPendingCharacters,
  clearPendingBattle,
  setTournamentLobbyMode,
  setPlayLobbyLockedMode,
  setBattleFinishedLogsLoading,
  setBattleFinishedLogs,
  appendBattleFinishedLogs,
  setLiveRankedBattleFeed,
  setLiveRankedBattleFeedLoading,
  setActiveRankedBattles,
  setActiveRankedBattlesLoading,
  prependActiveRankedBattleEntry,
  removeActiveRankedBattleEntry,
  setTodayRankedBattleChampions,
  setTodayRankedBattleChampionsLoading,
  setPvpMmrLeaderboard,
  appendPvpMmrLeaderboardEntries,
  appendLiveRankedBattleFeed,
  prependLiveRankedBattleFeedEntry,
  setRematchRequestPendingBattleId,
  markRematchAttemptUsed,
  setIncomingRematchRequest,
  setIsRespondingToRematch,
  setAcceptedRematch,
  setAcceptedRematchCountdown,
  setDeclinedRematchMessage,
  setIncomingDirectChallenge,
  setDirectChallengeRespondBusy,
  setDirectChallengeAwaitingJoin,
  setDirectChallengeDeclinedMessage,
  setDirectChallengeJoinerLock,
  setDirectChallengeActiveRoomId,
  setDirectChallengeBattleStartPending,
  clearDirectChallengeFlow,
  setIncomingFriendRequest,
  setFriendRequestRespondBusy,
  setFriendRequestDeclinedNotice,
} = pvpSlice.actions

export default pvpSlice.reducer

